import {
  CmsContentAboutContactSchema,
  type CmsContentAboutContactForm,
} from "@/modules/cms/domain/contracts";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";

type AboutContactFormProps = {
  content: CmsContentAboutContactForm | null;
  onSubmit: (content: CmsContentAboutContactForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutContactForm = {
  title: {},
  content: {},
  address: {},
  phone: "",
  email: "",
  mapsEmbedCode: "",
  socialLinks: [{}],
  isActive: true,
};

export function AboutContactForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutContactFormProps) {
  const methods = RHF.useForm<CmsContentAboutContactForm>({
    resolver: zodResolver(
      CmsContentAboutContactSchema,
    ) as Resolver<CmsContentAboutContactForm>,
    defaultValues: content ?? defaultContent,
  });

  return (
    <FormRoot<CmsContentAboutContactForm>
      methods={methods as UseFormReturn<CmsContentAboutContactForm>}
      onSubmit={onSubmit}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
          <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
          <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ເນື້ອຫາ (HTML)</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormTextarea
              name="content.lo"
              label="ລາວ"
              rows={3}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.th"
              label="ໄທ"
              rows={3}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.en"
              label="EN"
              rows={3}
              className="font-mono text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="address.lo" label="ທີ່ຢູ່ (ລາວ)" />
          <FormInput name="address.th" label="ที่อยู่ (ໄທ)" />
          <FormInput name="address.en" label="Address (EN)" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput name="phone" label="ເບີໂທ" placeholder="020 xxx xxxxx" />
          <FormInput
            name="email"
            label="ອີເມວ"
            type="email"
            placeholder="contact@example.com"
          />
        </div>
        <FormTextarea
          name="mapsEmbedCode"
          label="Maps embed code (iframe HTML)"
          rows={4}
          placeholder='<iframe src="..." ...></iframe>'
          className="font-mono text-sm"
        />
        <div className="space-y-2">
          <span className="font-medium text-sm">Social links</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              name="socialLinks.0.facebook"
              label="Facebook URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.instagram"
              label="Instagram URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.tiktok"
              label="TikTok URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.twitter"
              label="Twitter URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.youtube"
              label="YouTube URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.other"
              label="Other URL"
              placeholder="https://..."
            />
          </div>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
